// The Admin user form's request body, as a PURE function.
//
// Extracted from UserFormModal.submit() on 2026-08-27 so the create-versus-edit payload rules can
// be VERIFIED. They could not be before, and that is exactly how the clearable-field defect below
// survived: the backend has always honoured an explicit null, so any gate driving the SERVICE
// passes whether or not the form actually sends one. The bug lived entirely in this payload
// construction, so this is the piece that has to be testable.
//
// No React, no vite-only imports — plain TS, so database/verify-admin-user-clear.ts can import it.
//
// ─── THE RULE THIS FILE ENCODES ──────────────────────────────────────────────────────────────
// The service applies PRESENT-VERSUS-ABSENT discipline (`if (input.x !== undefined)` in
// admin-users.service updateAdminUser): an omitted key leaves the stored value alone, and an
// explicit null CLEARS it. So:
//
//   CREATE — omit empty optional fields. There is no stored value to clear, and omitting lets the
//            column take its default. This is correct and is left exactly as it was.
//   EDIT   — send an explicit NULL for every clearable field, so blanking a box actually clears it.
//
// Until 2026-08-27, preferred_name / pilot_code / mobile used the CREATE form on BOTH paths, so
// blanking one and saving silently kept the old value: no error, no warning, and a mistyped staff
// code or an obsolete mobile number was permanent. It affected every tenant. The neighbouring
// fields (home_base_id, approval_note, first_name / last_name / company) already did this
// correctly and are what the fix copies.

export type UserFormMode = 'create' | 'edit';

export interface UserFormValues {
  first_name?: string;
  last_name?: string;
  company?: string;
  preferred_name?: string;
  email?: string;
  mobile?: string;
  pilot_code?: string;
  home_base_id?: string;
  training_stage?: string;
  arn?: string;
  arn_pending?: boolean;
  approval_required?: boolean;
  approval_note?: string;
  ui_color?: string;
}

export interface BuildUserFormBodyInput {
  mode: UserFormMode;
  values: UserFormValues;
  roles: string[];
  isStudent: boolean;
  isStaff: boolean;
  confirmNew?: boolean;
}

/** Raw field read: the form stores strings, and a missing key reads as ''. Does NOT trim. */
const raw = (values: UserFormValues, k: keyof UserFormValues): string => {
  const val = values[k];
  return typeof val === 'string' ? val : '';
};

/**
 * A clearable free-text field's EDIT value: trimmed, and empty becomes null.
 *
 * The .trim() matters and is not decoration. The form's own reader does not trim, and the Zod
 * schema's `.trim()` would turn a whitespace-only box into "" rather than null — a third state
 * (neither set nor cleared) that nothing downstream wants. This is the approval_note pattern.
 */
const clearable = (values: UserFormValues, k: keyof UserFormValues): string | null =>
  raw(values, k).trim() || null;

export function buildUserFormBody(inp: BuildUserFormBodyInput): Record<string, unknown> {
  const { mode, values, roles, isStudent, isStaff, confirmNew } = inp;
  const create = mode === 'create';

  const first = raw(values, 'first_name').trim();
  const last = raw(values, 'last_name').trim();
  const company = raw(values, 'company').trim();
  const personName = first && last ? `${first} ${last}`.trim() : '';

  // full_name is kept in sync client-side (person name wins); the server re-derives it identically
  // via deriveFullName. On CREATE full_name is required. On EDIT these three CLEAR when emptied and
  // the server re-derives full_name from the patch. deriveFullName reads first_name / last_name /
  // company ONLY, so clearing preferred_name below can never affect full_name.
  const nameFields: Record<string, unknown> = create
    ? {
        full_name: personName || company,
        ...(first ? { first_name: first } : {}),
        ...(last ? { last_name: last } : {}),
        ...(company ? { company } : {}),
      }
    : {
        first_name: first || null,
        last_name: last || null,
        company: company || null,
        ...(personName ? { full_name: personName } : {}),
      };

  // THE FIX (2026-08-27). Omit-if-empty on create; explicit null on edit. See the header.
  const clearableFields: Record<string, unknown> = create
    ? {
        ...(raw(values, 'preferred_name') ? { preferred_name: raw(values, 'preferred_name') } : {}),
        ...(raw(values, 'mobile') ? { mobile: raw(values, 'mobile') } : {}),
        ...(raw(values, 'pilot_code') ? { pilot_code: raw(values, 'pilot_code') } : {}),
      }
    : {
        preferred_name: clearable(values, 'preferred_name'),
        mobile: clearable(values, 'mobile'),
        pilot_code: clearable(values, 'pilot_code'),
      };

  return {
    ...nameFields,
    ...clearableFields,
    email: raw(values, 'email'),
    roles,
    // B2 — always send home_base_id (explicit null when blank) so an existing default base can be
    // CLEARED on edit; the service writes null/disconnect. On create, blank → null (no base).
    home_base_id: raw(values, 'home_base_id') || null,
    ...(isStudent && raw(values, 'training_stage') ? { training_stage: raw(values, 'training_stage') } : {}),
    ...(isStudent && raw(values, 'arn') ? { arn: raw(values, 'arn') } : {}),
    ...(isStudent ? { arn_pending: !!values.arn_pending } : {}),
    // Staff "ask first" / PPR (Item 3) — staff roles only; empty reason clears the note (null).
    ...(isStaff ? { approval_required: !!values.approval_required, approval_note: clearable(values, 'approval_note') } : {}),
    // PR-000059 — the ops-board chip colour, staff roles only (a student has no instructor
    // colour). ⚠ SAME CREATE-VERSUS-EDIT RULE AS THE FIELDS ABOVE, and for the same reason:
    // clearing an assigned colour has to return the chip to its DERIVED colour, so the edit path
    // must send an explicit null. This is the fifth field of this shape — do not give it the
    // fault the first three had.
    ...(isStaff ? (create
      ? (raw(values, 'ui_color') ? { ui_color: raw(values, 'ui_color') } : {})
      : { ui_color: clearable(values, 'ui_color') }) : {}),
    ...(confirmNew ? { confirm_new: true } : {}),
  };
}
